'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useHasHydrated from '@/hooks/use-has-hydrated';
import { slugify } from '@/utils';

import MaxWidthWrapper from '@/components/MaxWidthWrapper';
import Spacer from '@/components/Spacer';
import Paragraph from '@/components/Paragraph';

import Match from '@/components/Goodies/OperatorLookup/Match';
import OperatorList from '@/components/Goodies/OperatorLookup/OperatorList';
import Search from '@/components/Goodies/OperatorLookup/Search';
import type { OperatorWithSnippet } from './OperatorLookup.types';

function OperatorLookup({
  data,
}: {
  data: Record<string, OperatorWithSnippet>;
}) {
  const hasHydrated = useHasHydrated();
  const [isFocused, setIsFocused] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [matchedOperator, setMatchedOperator] =
    React.useState<OperatorWithSnippet | null>(null);
  const [showNoMatchMethod, setShowNoMatchMethod] =
    React.useState<boolean>(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [animateSuggestions, setAnimateSuggestions] =
    React.useState(true);

  const hasMounted = React.useRef(false);

  const prefersReducedMotion = usePrefersReducedMotion();

  let trimmedSearch = searchValue.trim();

  React.useEffect(() => {
    let timeoutId: number;

    if (showNoMatchMethod) {
      setShowNoMatchMethod(false);
    }

    const match = data[trimmedSearch];

    if (match) {
      // Immediately set new matches
      setMatchedOperator(match);

      if (!hasSearched) {
        setHasSearched(true);
      }

      if (animateSuggestions) {
        setAnimateSuggestions(false);
      }
    } else {
      // …But! If there isn't a match, add a short delay before
      // unsetting it. This prevents a lot of flickering.
      timeoutId = window.setTimeout(() => {
        setMatchedOperator(null);

        // Wait another couple secs before showing a message about
        // not finding any results
        timeoutId = window.setTimeout(() => {
          if (trimmedSearch) {
            setShowNoMatchMethod(true);
          }
        }, 2000);
      }, 500);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
    // NOTE: this shouldn't be an effect. I should do this work in the event handler. It's missing lots of deps, but I can't add them since I only want this work to run when `searchValue` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedSearch]);

  // This is our hacky routing solution
  React.useEffect(() => {
    // Don't fire on initial mount
    if (!hasMounted.current) {
      return;
    }

    const match = data[trimmedSearch];

    if (match) {
      history.pushState(
        { operator: trimmedSearch },
        '',
        `/operator-lookup?match=${slugify(match.label)}`
      );
    } else {
      history.pushState({}, '', `/operator-lookup/`);
    }
  }, [trimmedSearch, data]);

  // Another effect for managing pushstate stuff
  React.useEffect(() => {
    // Did we start with a selected operator?
    const { search } = window.location;
    const match = search.match(/\?match=(.+)/);

    if (match) {
      const [, slugifiedLabel] = match;

      const matchingOperator = Object.entries(data).find(
        ([, operatorInfo]) => {
          return slugify(operatorInfo.label) === slugifiedLabel;
        }
      );

      if (matchingOperator) {
        setSearchValue(matchingOperator[0]);
      }
    }

    window.onpopstate = function (event) {
      setSearchValue(event.state.operator || '');
    };

    return () => {
      window.onpopstate = null;
    };
  }, [data]);

  React.useEffect(() => {
    hasMounted.current = true;
  }, []);

  const handleSearch = React.useCallback((val: string) => {
    setSearchValue(val.replace(/[a-zA-Z]*/g, ''));
  }, []);
  const handleClear = React.useCallback(() => {
    setSearchValue('');
    setMatchedOperator(null);
  }, []);

  // NOTE: This *appears* broken in development, thanks to Strict Mode. Everything is hidden under an `opacity: 0` wrapper. But it works fine in production.

  return (
    <LayoutGroup>
      <Wrapper maxWidth={650}>
        <AnimatePresence>
          {!matchedOperator && (
            <Header
              key="header"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Heading>Operator Lookup</Heading>
              <Spacer size={8} />
              <Paragraph>
                Enter a JavaScript operator to learn more about it:
              </Paragraph>
              <Spacer size={32} />
            </Header>
          )}
        </AnimatePresence>
        <SearchWrapper
          animate={{ scale: isFocused ? 1.025 : 1 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 30,
            restDelta: 0.001,
            restSpeed: 0.001,
          }}
        >
          <Search
            prefersReducedMotion={prefersReducedMotion}
            searchValue={searchValue}
            isFocused={isFocused}
            handleSearch={handleSearch}
            handleClear={handleClear}
            handleFocus={setIsFocused}
          />
        </SearchWrapper>
        {showNoMatchMethod && (
          <NoMatch
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: -20 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 30,
              restDelta: 0.01,
              restSpeed: 0.01,
            }}
          >
            <strong>Sorry, that operator isn't recognized!</strong>
            <br />
            (It's entirely possible that this list is incomplete.)
          </NoMatch>
        )}
        {!matchedOperator &&
          (hasHydrated ? (
            <OperatorList
              handleClick={setSearchValue}
              prefersReducedMotion={prefersReducedMotion}
              animateSuggestions={animateSuggestions}
            />
          ) : (
            <ListSpacer />
          ))}
        {matchedOperator && (
          <motion.div
            initial={{ opacity: 0, y: 200 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 200 }}
          >
            <Match {...matchedOperator} />
          </motion.div>
        )}
      </Wrapper>
    </LayoutGroup>
  );
}

const Wrapper = styled(MaxWidthWrapper)`
  padding-block: 96px;
  text-align: center;
`;

const Header = styled(motion.header)`
  position: relative;
  z-index: 1;
`;

const SearchWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
`;

const Heading = styled.h1`
  font-size: 2rem;
  color: var(--color-gray-1000);
`;

const NoMatch = styled(motion.p)`
  margin-top: 48px;
  color: var(--color-gray-900);
  will-change: transform;
`;

const ListSpacer = styled.div`
  height: 11.75rem;
  margin-top: 96px;
  margin-bottom: 48px;
`;

export default OperatorLookup;
